import { autoinject } from "aurelia-framework";
import { ApiResponse } from "../models/api-response";
import { ServiceResponse } from "../models/service-response";
import { BaseService } from "./base-service";
import { RestServiceClient } from "./rest-service-client";
import { State } from './global-state';
import { ICustomerAccountToPay } from "./hpf-transaction-service";

@autoinject
export class TemplateService extends BaseService {
	globalState: State;

	constructor(private http: RestServiceClient, state: State) {
		super();
		this.globalState = state;
	}

	applyTemplate(request: ApplyTemplateBaseRequest): Promise<ServiceResponse> {
		let url = `/portal/${this.globalState.business.portalId}/application/template`;
		return this.http.post(url, request)
			.then((apiResponse: ApiResponse) => {
				if (!apiResponse.isSuccessful) {
					return this.error(`Unable to get template: ${apiResponse.getErrorMessage()}`);
				}
				return this.success('', apiResponse.value);
			});
	}

	getPortalTemplateType(paymentInformation: any, isSingleFuture: boolean): PortalTemplateTypes {
		if (!paymentInformation) {
			return null;
		}

		const isCard: boolean = !!paymentInformation.card || paymentInformation.digitalWalletSettings?.isCard();

		if (isSingleFuture) {
			return isCard ? PortalTemplateTypes.CreditCardInstallmentPlanReceipt : PortalTemplateTypes.CheckInstallmentPlanReceipt;
		}

		return isCard ? PortalTemplateTypes.CreditCardSinglePaymentReceipt : PortalTemplateTypes.CheckSinglePaymentReceipt;
	}
}

@autoinject
export abstract class ApplyTemplateBaseRequest {
	protected templateType: PortalTemplateTypes;
	isForNotification: boolean;
	savedPaymentMethodToWallet: boolean;
	isEzpay: boolean;
}

export class ApplyCardSinglePaymentTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.CreditCardSinglePaymentReceipt;
	pnref: number;
	accountPayments: ICustomerAccountToPay[];
}

export class ApplyCheckSinglePaymentTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.CheckSinglePaymentReceipt;
	pnref: number;
	accountPayments: ICustomerAccountToPay[];
}

export class ApplyCardInstallmentPlanReceiptTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.CreditCardInstallmentPlanReceipt;
	planId: string;
	merchantKey: number;
	accountPayments: ICustomerAccountToPay[];
}

export class ApplyCheckInstallmentPlanReceiptTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.CheckInstallmentPlanReceipt;
	planId: string;
	merchantKey: number;
	accountPayments: ICustomerAccountToPay[];
}

export class ApplyCardRecurringPlanReceiptTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.CreditCardRecurringPaymentPlanReceipt;
	planId: string;
	merchantKey: number;
}

export class ApplyCheckRecurringPlanReceiptTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.CheckRecurringPaymentPlanReceipt;
	autopayId: string;
	planStartDate: Date;
	planEndDate: Date;
	numberOfPayments: number;
	mechantDescriptor: string;
	planFrequency: string;
	paymentAmountLimit: number;
	serviceFee: number;
	accountName: string;
	customerAccountNumber: number;
	paymentMethod: string;
	zipCode: string;
}

export class ApplyAutopayPlanReceiptTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.AutopayPlanReceipt;
	autopayKey: string;
	merchantKey: number;
}

export class ApplyTransactionFailedTemplateRequest extends ApplyTemplateBaseRequest {
	protected templateType = PortalTemplateTypes.TransactionFailed;
	resultCode: string;
	resultMessage: string;
}

export enum PortalTemplateTypes {
	CheckSinglePaymentReceipt = 101,
	CheckInstallmentPlanReceipt = 102,
	CreditCardSinglePaymentReceipt = 103,
	CreditCardInstallmentPlanReceipt = 104,
	CreditCardRecurringPaymentPlanReceipt = 119,
	CheckRecurringPaymentPlanReceipt = 120,
	AutopayPlanReceipt = 127,
	TransactionFailed = 999
}

export enum PortalTemplateTypesNames {
	AutopayPlanReceipt = 'Autopay Plan Receipt'
}
